import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

// A redline revision cloud: the drafting mark for "this is where the change is".
// Scallops are computed from the real box so the cloud fits any content size.
function cloudPath(w: number, h: number, step: number) {
	const edge = (len: number) => Math.max(1, Math.round(len / step));
	const nx = edge(w);
	const ny = edge(h);
	const sx = w / nx;
	const sy = h / ny;
	const r = (s: number) => (s * 0.62).toFixed(2);
	let d = 'M0 0';
	for (let i = 1; i <= nx; i++) d += ` A${r(sx)} ${r(sx)} 0 0 1 ${(i * sx).toFixed(2)} 0`;
	for (let i = 1; i <= ny; i++) d += ` A${r(sy)} ${r(sy)} 0 0 1 ${w} ${(i * sy).toFixed(2)}`;
	for (let i = nx - 1; i >= 0; i--) d += ` A${r(sx)} ${r(sx)} 0 0 1 ${(i * sx).toFixed(2)} ${h}`;
	for (let i = ny - 1; i >= 0; i--) d += ` A${r(sy)} ${r(sy)} 0 0 1 0 ${(i * sy).toFixed(2)}`;
	return d;
}

interface RevisionCloudProps {
	children: ReactNode;
	tag?: string;
	className?: string;
}

export default function RevisionCloud({ children, tag, className = '' }: RevisionCloudProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [size, setSize] = useState({ w: 0, h: 0 });

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		// The first observer callback lands after layout but before paint, so the cloud
		// appears in the same frame without forcing a synchronous reflow here.
		const observer = new ResizeObserver(([entry]) => {
			const box = entry.contentRect;
			setSize({ w: Math.round(box.width), h: Math.round(box.height) });
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const pad = 12;
	return (
		<div className={`relative ${className}`}>
			<div ref={ref}>{children}</div>
			{size.w > 0 && (
				<svg aria-hidden='true' className='pointer-events-none absolute overflow-visible' style={{ left: -pad, top: -pad }} width={size.w + pad * 2} height={size.h + pad * 2}>
					<path d={cloudPath(size.w + pad * 2, size.h + pad * 2, 26)} fill='none' className='plot-line stroke-redline' strokeWidth={1.5} pathLength={1} />
				</svg>
			)}
			{tag && (
				<span className='lettering absolute top-[-44px] right-2 flex items-center gap-2 bg-sheet px-1.5 py-0.5 text-label text-redline'>
					<svg aria-hidden='true' viewBox='0 0 24 22' className='h-[18px] w-[20px]'>
						<path d='M12 1.5 22.5 20.5h-21Z' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinejoin='round' />
						<text x='12' y='17' textAnchor='middle' fontSize='9.5' fontWeight='700' fill='currentColor'>
							1
						</text>
					</svg>
					{tag}
				</span>
			)}
		</div>
	);
}
